/**
 * Certifications insights section: Employee summary tabs, DRI Insights (EmployeeWise) product grid,
 * then Certifications + Skill Badges tables (separate tabbed panel below).
 * Owns certification month range state and all trend computations (monthly, active, expiring).
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { SEARCE_COLORS } from '../../theme';
import { countCertificationMetrics } from '../../utils/skillsCertsTabMetrics';
import { SkillsCertsTabSummaryChips } from '../common/SkillsCertsTabSummaryChips';
import CertificationsAnalysis from './CertificationsAnalysis';
import EmployeeWiseDriInsightsPanel from './EmployeeWiseDriInsightsPanel';
import type { IndividualDetailsFilterState, IndividualDetailsFilterHandlers } from '../tables/driInsights/IndividualDetailsTable';
import EmployeeTrainingsSummaryTable from '../tables/driInsights/EmployeeTrainingsSummaryTable';
import { mapUnregisteredEmployeesToTrainingSummaryRows } from '../../utils/driInsightsUtils';
import CertificationsTable from '../tables/driInsights/CertificationsTable';
import SkillBadgesTable from '../tables/driInsights/SkillBadgesTable';
import type {
  CertificationRecord,
  MonthRange,
  DRIValueType,
  IndividualDetailsRow,
  UnregisteredEmployeeRow,
  EmployeeTrainingSummary,
  SkillBadgeRecord,
  Tier1TrendRecord,
  CompetencyTrendRecord,
} from '../../types/driInsights';
import type { FilterRequest } from '../../types/api';
import { MONTH_NAMES, MONTH_ABBREVIATIONS } from '../../constants/driInsightsConstants';

export interface CertificationsInsightsSectionProps {
  certificationsData: CertificationRecord[];
  defaultCertificationMonths: { startMonth: MonthRange; endMonth: MonthRange };
  activeFilters: FilterRequest;
  formatDate: (dateString: string | null | undefined) => string;
  gridCellValueType: DRIValueType;
  onGridCellValueTypeChange: (value: DRIValueType) => void;
  onClearAllFilters: () => void;
  individualDetailsActiveFilterCount: number;
  individualDetailsRows: IndividualDetailsRow[];
  gridColumns: string[];
  onFilterClick: (event: React.MouseEvent<HTMLElement>, columnId: string, useCheckbox?: boolean, isNumeric?: boolean, isDateRange?: boolean) => void;
  individualDetailsFilterState: IndividualDetailsFilterState;
  individualDetailsFilterHandlers: IndividualDetailsFilterHandlers;
  individualDetailsPaginationResetDeps?: unknown;

  // Unregistered Employees (merged into employee summary table)
  unregisteredTableRef: React.RefObject<HTMLDivElement | null>;
  isLoadingUnregistered: boolean;
  unregisteredEmployeesData: UnregisteredEmployeeRow[];

  // Employee Trainings Summary
  employeeTrainingsSummaryData: EmployeeTrainingSummary[];

  // Certifications + Skill Badges tables
  isLoadingCertsAndSkills: boolean;
  skillBadgesData: SkillBadgeRecord[];

  // Tier1 and Competency trend data for Employee Summary table columns
  tier1TrendData?: Tier1TrendRecord[];
  competencyTrendData?: CompetencyTrendRecord[];
}

const CertificationsInsightsSection: React.FC<CertificationsInsightsSectionProps> = ({
  certificationsData,
  defaultCertificationMonths,
  activeFilters,
  formatDate,
  gridCellValueType,
  onGridCellValueTypeChange,
  onClearAllFilters,
  individualDetailsActiveFilterCount,
  individualDetailsRows,
  gridColumns,
  onFilterClick,
  individualDetailsFilterState,
  individualDetailsFilterHandlers,
  individualDetailsPaginationResetDeps,
  unregisteredTableRef,
  isLoadingUnregistered,
  unregisteredEmployeesData,
  employeeTrainingsSummaryData,
  isLoadingCertsAndSkills,
  skillBadgesData,
  tier1TrendData = [],
  competencyTrendData = [],
}) => {
  const getCertificationsForEmployee = useCallback(
    (email: string) => certificationsData.filter((c: CertificationRecord) => c.employee_email === email),
    [certificationsData]
  );

  const [certificationStartMonth, setCertificationStartMonth] = useState<MonthRange>(defaultCertificationMonths.startMonth);
  const [certificationEndMonth, setCertificationEndMonth] = useState<MonthRange>(defaultCertificationMonths.endMonth);
  const [certificationChartType, setCertificationChartType] = useState<'new' | 'active' | 'expiring'>('new');
  const [certificationsSkillBadgesTab, setCertificationsSkillBadgesTab] = useState(0);

  const certMetrics = useMemo(
    () => countCertificationMetrics(certificationsData ?? []),
    [certificationsData]
  );

  const mergedEmployeeTrainingsSummaryData = useMemo(() => {
    const registeredEmails = new Set(
      (employeeTrainingsSummaryData ?? []).map((r) => r.employee_email).filter(Boolean)
    );
    const unregisteredRows = mapUnregisteredEmployeesToTrainingSummaryRows(
      unregisteredEmployeesData ?? [],
      certificationsData ?? [],
      skillBadgesData ?? [],
      registeredEmails
    );
    return [...(employeeTrainingsSummaryData ?? []), ...unregisteredRows];
  }, [
    employeeTrainingsSummaryData,
    unregisteredEmployeesData,
    certificationsData,
    skillBadgesData,
  ]);
  const skillCount = skillBadgesData?.length ?? 0;
  const showSkillPills = !isLoadingCertsAndSkills;
  const showCertPills = !isLoadingCertsAndSkills;
  const showTabSummaryChips = showSkillPills || showCertPills;

  // Calculate Monthly Certifications Trend (new certs obtained per month)
  const monthlyCertificationsTrend = useMemo(() => {
    if (!certificationsData || certificationsData.length === 0) return [];

    const startMonth = certificationStartMonth || defaultCertificationMonths.startMonth;
    const endMonth = certificationEndMonth || defaultCertificationMonths.endMonth;
    if (!startMonth || !endMonth) return [];

    const certs = certificationsData;
    const targetStartYear = startMonth.year;
    const targetStartMonth = startMonth.month;
    const targetEndYear = endMonth.year;
    const targetEndMonth = endMonth.month;

    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = targetStartYear;
    let currentMonth = targetStartMonth;

    while (
      currentYear < targetEndYear ||
      (currentYear === targetEndYear && currentMonth <= targetEndMonth)
    ) {
      months.push({
        month: MONTH_NAMES[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${MONTH_ABBREVIATIONS[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return months.map(monthData => {
      const targetMonthIndex = MONTH_NAMES.indexOf(monthData.month) + 1;
      const count = certs.filter((cert: CertificationRecord) => {
        if (!cert.issue_date) return false;
        const issueDate = new Date(cert.issue_date);
        const issueYear = issueDate.getFullYear();
        const issueMonth = issueDate.getMonth() + 1;
        return issueYear === monthData.year && issueMonth === targetMonthIndex;
      }).length;
      return { ...monthData, count };
    });
  }, [certificationsData, certificationStartMonth, certificationEndMonth, defaultCertificationMonths.startMonth, defaultCertificationMonths.endMonth]);

  // Calculate Active Certifications Trend (active in each month)
  const activeCertificationsTrend = useMemo(() => {
    if (!certificationsData || certificationsData.length === 0) return [];

    const startMonth = certificationStartMonth || defaultCertificationMonths.startMonth;
    const endMonth = certificationEndMonth || defaultCertificationMonths.endMonth;
    if (!startMonth || !endMonth) return [];

    const certs = certificationsData;
    const targetStartYear = startMonth.year;
    const targetStartMonth = startMonth.month;
    const targetEndYear = endMonth.year;
    const targetEndMonth = endMonth.month;

    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = targetStartYear;
    let currentMonth = targetStartMonth;

    while (
      currentYear < targetEndYear ||
      (currentYear === targetEndYear && currentMonth <= targetEndMonth)
    ) {
      months.push({
        month: MONTH_NAMES[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${MONTH_ABBREVIATIONS[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return months.map(monthData => {
      const targetMonthIndex = MONTH_NAMES.indexOf(monthData.month) + 1;
      const monthDate = new Date(monthData.year, targetMonthIndex - 1, 1);
      const nextMonthDate = new Date(monthData.year, targetMonthIndex, 1);
      const count = certs.filter((cert: CertificationRecord) => {
        if (!cert.issue_date) return false;
        const issueDate = new Date(cert.issue_date);
        const expiryDate = cert.expiry_date ? new Date(cert.expiry_date) : null;
        const wasIssuedByEnd = issueDate < nextMonthDate;
        const hasNotExpiredByStart = !expiryDate || expiryDate >= monthDate;
        return wasIssuedByEnd && hasNotExpiredByStart;
      }).length;
      return { ...monthData, count };
    });
  }, [certificationsData, certificationStartMonth, certificationEndMonth, defaultCertificationMonths.startMonth, defaultCertificationMonths.endMonth]);

  // Calculate Expiring Certifications Trend (expiring in each month)
  const expiringCertificationsTrend = useMemo(() => {
    if (!certificationsData || certificationsData.length === 0) return [];

    const startMonth = certificationStartMonth || defaultCertificationMonths.startMonth;
    const endMonth = certificationEndMonth || defaultCertificationMonths.endMonth;
    if (!startMonth || !endMonth) return [];

    const certs = certificationsData;
    const targetStartYear = startMonth.year;
    const targetStartMonth = startMonth.month;
    const targetEndYear = endMonth.year;
    const targetEndMonth = endMonth.month;

    const months: { month: string; year: number; monthAbbr: string; sortKey: string }[] = [];
    let currentYear = targetStartYear;
    let currentMonth = targetStartMonth;

    while (
      currentYear < targetEndYear ||
      (currentYear === targetEndYear && currentMonth <= targetEndMonth)
    ) {
      months.push({
        month: MONTH_NAMES[currentMonth - 1],
        year: currentYear,
        monthAbbr: `${MONTH_ABBREVIATIONS[currentMonth - 1]} ${currentYear}`,
        sortKey: `${currentYear}-${String(currentMonth).padStart(2, '0')}`,
      });
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return months.map(monthData => {
      const targetMonthIndex = MONTH_NAMES.indexOf(monthData.month) + 1;
      const count = certs.filter((cert: CertificationRecord) => {
        if (!cert.expiry_date) return false;
        const expiryDate = new Date(cert.expiry_date);
        const expiryYear = expiryDate.getFullYear();
        const expiryMonth = expiryDate.getMonth() + 1;
        return expiryYear === monthData.year && expiryMonth === targetMonthIndex;
      }).length;
      return { ...monthData, count };
    });
  }, [certificationsData, certificationStartMonth, certificationEndMonth, defaultCertificationMonths.startMonth, defaultCertificationMonths.endMonth]);

  return (
    <Box>
      <EmployeeWiseDriInsightsPanel
        gridCellValueType={gridCellValueType}
        onGridCellValueTypeChange={onGridCellValueTypeChange}
        onClearAllFilters={onClearAllFilters}
        activeFilterCount={individualDetailsActiveFilterCount}
        rows={individualDetailsRows}
        gridColumns={gridColumns}
        onFilterClick={onFilterClick}
        filterState={individualDetailsFilterState}
        filterHandlers={individualDetailsFilterHandlers}
        paginationResetDeps={individualDetailsPaginationResetDeps}
      />


      {/* Employee summary — registered + unregistered in one table */}
      <Box ref={unregisteredTableRef} sx={{ mb: 2, mt: 3 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: SEARCE_COLORS.white,
            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
            borderRadius: 0,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, px: 2, pt: 2, pb: 0.5 }}
          >
            Employee summary
          </Typography>
          <Box
            id="employee-summary-panel-unified"
            sx={{
              px: 2,
              pb: 2,
              pt: 1,
              width: '100%',
            }}
          >
            <EmployeeTrainingsSummaryTable
              hideTitle
              data={mergedEmployeeTrainingsSummaryData}
              getCertificationsForEmployee={getCertificationsForEmployee}
              formatDate={formatDate}
              tier1TrendData={tier1TrendData}
              competencyTrendData={competencyTrendData}
              isLoadingUnregistered={isLoadingUnregistered}
            />
          </Box>
        </Paper>
      </Box>

     
      {/* Certifications / analysis / skill badges: tabbed panel */}
      <Box sx={{ mb: 4, mt: 3 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: SEARCE_COLORS.white,
            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
            borderRadius: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'clamp(520px, 65vh, 900px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'flex-end' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 1.5 },
              flexShrink: 0,
              borderBottom: `1px solid ${SEARCE_COLORS.neutral_200}`,
              backgroundColor: SEARCE_COLORS.background,
              px: { xs: 1, sm: 2 },
              pt: 0.5,
              pb: 0,
            }}
          >
            <Tabs
              value={certificationsSkillBadgesTab}
              onChange={(_, v) => setCertificationsSkillBadgesTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Certifications and skill badges sections"
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                minHeight: 48,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 48,
                  fontSize: '0.95rem',
                  color: SEARCE_COLORS.textSecondary,
                  fontWeight: 400,
                },
                '& .MuiTab-root.Mui-selected': {
                  color: `${SEARCE_COLORS.primary} !important`,
                  fontWeight: 700,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: SEARCE_COLORS.primary,
                  height: 3,
                },
              }}
            >
              <Tab label="Certifications Analysis" id="certs-tab-analysis" aria-controls="certs-panel-analysis" />
              <Tab label="Certifications" id="certs-tab-list" aria-controls="certs-panel-list" />
              <Tab label="Skill Badges" id="certs-tab-badges" aria-controls="certs-panel-badges" />
            </Tabs>
            {showTabSummaryChips && (
              <SkillsCertsTabSummaryChips
                skillCount={skillCount}
                certMetrics={certMetrics}
                showSkillPills={showSkillPills}
                showCertPills={showCertPills}
              />
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              pt: 2,
              px: 0,
            }}
          >
          {certificationsSkillBadgesTab === 0 && (
            <Box
              role="tabpanel"
              id="certs-panel-analysis"
              aria-labelledby="certs-tab-analysis"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxWidth: { xs: '100%', md: '50%' },
                minWidth: 0,
                alignSelf: 'flex-start',
                px: 0,
                pb: 2,
              }}
            >
              <CertificationsAnalysis filters={activeFilters} embedded />
            </Box>
          )}
          {certificationsSkillBadgesTab === 1 && (
            <Box
              role="tabpanel"
              id="certs-panel-list"
              aria-labelledby="certs-tab-list"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                px: 0,
                pb: 2,
              }}
            >
              <CertificationsTable
                isLoading={isLoadingCertsAndSkills}
                data={certificationsData}
                formatDate={formatDate}
              />
            </Box>
          )}
          {certificationsSkillBadgesTab === 2 && (
            <Box
              role="tabpanel"
              id="certs-panel-badges"
              aria-labelledby="certs-tab-badges"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                px: 0,
                pb: 2,
              }}
            >
              <SkillBadgesTable
                isLoading={isLoadingCertsAndSkills}
                data={skillBadgesData}
                formatDate={formatDate}
                embedded
              />
            </Box>
          )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CertificationsInsightsSection;
